import React, { useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Spin } from "antd";

import { Select } from "../../../../components/shared";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import { useSportsbookRestrictionDropdown } from "../../../../queries/dynamicBonuses/useSportsbookRestrictionDropdown";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import type { SportsbookSearchType } from "../constants";
import styles from "../stylesDynamicBonuses.module.css";
import {
  rememberRestrictionDisplayName,
  usableRestrictionDisplayName,
  useRestrictionDisplayName,
} from "./restrictionDisplayNames";

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  value?: string;
  onChange?: (value?: string) => void;
  searchType: SportsbookSearchType;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
};

const optionLabel = (id: string, name?: string): string => {
  const title = usableRestrictionDisplayName(id, name);
  return title ? `${title} (${id})` : id;
};

/**
 * Single-select sportsbook ID search for Destination (BONENG-1064).
 * Reuses the restriction dropdown API so tournament/match IDs can be
 * picked by name; a typed Sportradar ID is still selectable.
 */
const SportsbookDeeplinkIdSelect: React.FC<Props> = ({
  value,
  onChange,
  searchType,
  placeholder,
  id,
  disabled,
}) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const { data: options = [], isLoading } = useSportsbookRestrictionDropdown(
    searchType,
    debouncedSearch,
  );
  const cachedName = useRestrictionDisplayName(value);

  const selectOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: { label: string; value: string }[] = [];
    const typed = search.trim();
    if (value && !seen.has(value)) {
      seen.add(value);
      const fromResults = options.find((opt) => opt.id === value)?.name;
      rows.push({
        label: optionLabel(value, cachedName ?? fromResults),
        value,
      });
    }
    if (typed && !seen.has(typed)) {
      seen.add(typed);
      rows.push({ label: typed, value: typed });
    }
    for (const opt of options) {
      if (seen.has(opt.id)) continue;
      seen.add(opt.id);
      rows.push({ label: optionLabel(opt.id, opt.name), value: opt.id });
    }
    return rows;
  }, [cachedName, options, search, value]);

  return (
    <Select
      id={id}
      allowClear
      showSearch
      disabled={disabled}
      value={value || undefined}
      searchValue={search}
      onSearch={setSearch}
      onChange={(next) => {
        const selected = typeof next === "string" && next.trim() ? next.trim() : undefined;
        if (selected) {
          const name = options.find((opt) => opt.id === selected)?.name;
          if (name) rememberRestrictionDisplayName(selected, name);
        }
        onChange?.(selected);
        setSearch("");
      }}
      options={selectOptions}
      filterOption={false}
      placeholder={
        placeholder ??
        formatString(AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SEARCH_PLACEHOLDER, {
          label: searchType,
        })
      }
      notFoundContent={
        isLoading ? <Spin size="small" /> : AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_NO_RESULTS
      }
      suffixIcon={<SearchOutlined />}
      className={styles.sportsbookRestrictionSelect}
      data-testid={`sportsbook-deeplink-id-${searchType}`}
    />
  );
};

export default SportsbookDeeplinkIdSelect;
